import { useEffect, useMemo, useState } from "react";
import { Dashboard } from "./components/Dashboard";
import { LoginScreen } from "./components/LoginScreen";
import { LogsPage } from "./components/Logs";
import { AddServerModal, DeleteModal, EditServerModal } from "./components/Modals";
import { ProcessPanel } from "./components/ProcessPanel";
import { SettingsPage } from "./components/Settings";
import { Header, Sidebar } from "./components/Shell";
import {
  TweakRadio,
  TweakSection,
  TweakSelect,
  TweakSlider,
  TweakToggle,
  TweaksPanel,
  useTweaks,
} from "./components/Tweaks";
import { useAuth } from "./hooks/useAuth";
import { useNotifications } from "./hooks/useNotifications";
import { useServers } from "./hooks/useServers";
import { useSettings } from "./hooks/useSettings";
import { api } from "./lib/api";
import { formatRelativeTime } from "./lib/format";
import { statusOf } from "./lib/status";

const ROUTE_TITLES = {
  dashboard: { title: "Dashboard", sub: "Monitoring real-time semua server" },
  notifications: { title: "Pengaturan Notifikasi", sub: "Konfigurasi Telegram & threshold" },
  logs: { title: "Log Notifikasi", sub: "Riwayat alert yang dikirim" },
};

export default function App() {
  const auth = useAuth();

  // Theme bootstrap runs even pre-login so the LoginScreen respects dark/light.
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", "dark");
  }, []);

  if (auth.loading) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--bg)", color: "var(--text-3)", fontSize: 13 }}>
        Memuat…
      </div>
    );
  }

  if (auth.authRequired && !auth.authenticated) {
    return <LoginScreen onLogin={auth.login} />;
  }

  return <Authenticated logout={auth.logout} authRequired={auth.authRequired} />;
}

function Authenticated({ logout, authRequired }) {
  const [t, setTweak] = useTweaks({
    theme: "dark",
    density: "cozy",
    view: "table",
    tickSeconds: 3,
    sidebarExpanded: true,
    accent: "teal",
  });

  // ----- Server state (real data from hub) -----
  const { servers: liveServers, pulses, loading: loadingServers, error: serverError, now } = useServers({
    tickSeconds: t.tickSeconds,
  });

  // ----- Persistent settings (thresholds, telegram, notify toggles) -----
  const { settings, save: saveSettings, lastSavedAt } = useSettings();

  // Derive a plain `{cpu, ram, disk}` thresholds object — that's what
  // Dashboard / ProcessPanel / Modals consume. App.jsx doesn't need the
  // full settings shape anywhere else.
  const thresholds = useMemo(
    () => ({
      cpu: settings.thresholdCpu,
      ram: settings.thresholdRam,
      disk: settings.thresholdDisk,
    }),
    [settings.thresholdCpu, settings.thresholdRam, settings.thresholdDisk],
  );

  // ----- Notification log -----
  const { logs, loading: loadingLogs, error: logsError, refresh: refreshLogs } = useNotifications({
    intervalSeconds: 15,
  });

  // The hub already filters archived servers from /dashboard/servers, so
  // liveServers is the visible set. No local "hidden" overlay needed.
  const servers = liveServers;

  // ----- UI state -----
  const [route, setRoute] = useState("dashboard");
  const [openServerId, setOpenServerId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState(null);

  // ----- Theme sync -----
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", t.theme);
  }, [t.theme]);

  // ----- Accent sync -----
  useEffect(() => {
    const map = {
      teal: ["oklch(0.78 0.12 195)", "oklch(0.72 0.14 195)"],
      indigo: ["oklch(0.7 0.16 270)", "oklch(0.62 0.18 270)"],
      lime: ["oklch(0.85 0.18 130)", "oklch(0.78 0.2 130)"],
      orange: ["oklch(0.78 0.16 50)", "oklch(0.72 0.18 50)"],
    };
    const [a, b] = map[t.accent] || map.teal;
    document.documentElement.style.setProperty("--accent", a);
    document.documentElement.style.setProperty("--accent-strong", b);
    document.documentElement.style.setProperty("--accent-soft", a.replace(/\)$/, " / 0.12)"));
    document.documentElement.style.setProperty("--focus", a.replace(/\)$/, " / 0.45)"));
  }, [t.accent]);

  // ----- Derived -----
  const statusCounts = useMemo(() => {
    const counts = { ok: 0, warn: 0, crit: 0, offline: 0, total: servers.length };
    for (const s of servers) {
      const st = statusOf(s, thresholds);
      counts[st]++;
    }
    return counts;
  }, [servers, thresholds]);

  const problemCount = statusCounts.crit + statusCounts.warn;
  const openServer = servers.find((s) => s.id === openServerId) ?? null;

  // ----- Handlers -----
  const handleDelete = async (s) => {
    await api.deleteServer(s.id);
    setDeleteTarget(null);
    if (openServerId === s.id) setOpenServerId(null);
    // The next useServers poll won't include this id (status='archived'
    // is filtered server-side).
  };

  const lastTickStr = formatRelativeTime(now - 500);
  const routeInfo = ROUTE_TITLES[route];

  // ----- Banner for connection / empty states -----
  const connBanner = (() => {
    if (loadingServers && liveServers.length === 0) {
      return <Banner tone="info">Menyambung ke hub…</Banner>;
    }
    if (serverError) {
      return (
        <Banner tone="crit">
          Gagal mengambil data dari hub: {serverError.message}
          {serverError.status ? ` (HTTP ${serverError.status})` : ""}. Polling akan dicoba lagi tiap {t.tickSeconds}s.
        </Banner>
      );
    }
    if (!loadingServers && liveServers.length === 0) {
      return (
        <Banner tone="warn">
          Belum ada server terdaftar di hub. Jalankan agent dengan token yang dibuat via{" "}
          <span className="mono">bun run seed:agent</span>{" "}
          atau spawn fleet dengan <span className="mono">bun run simulate:fleet</span>.
        </Banner>
      );
    }
    return null;
  })();

  return (
    <div className="app">
      <Sidebar
        route={route}
        setRoute={setRoute}
        problemCount={problemCount}
        expanded={t.sidebarExpanded}
        setExpanded={(v) => setTweak("sidebarExpanded", v)}
      />
      <main className="main">
        <Header
          title={routeInfo.title}
          sub={
            route === "dashboard"
              ? `${servers.length} server · ${statusCounts.crit} critical, ${statusCounts.warn} warning`
              : routeInfo.sub
          }
          lastTick={route === "dashboard" ? lastTickStr : null}
          theme={t.theme}
          setTheme={(v) => setTweak("theme", v)}
          statusCounts={route === "dashboard" ? statusCounts : null}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          right={
            authRequired ? (
              <button
                type="button"
                onClick={logout}
                title="Logout"
                style={{
                  fontSize: 12,
                  padding: "6px 10px",
                  borderRadius: 8,
                  border: "1px solid var(--border)",
                  background: "transparent",
                  color: "var(--text-2)",
                  cursor: "pointer",
                }}
              >
                Keluar
              </button>
            ) : null
          }
        />

        {route === "dashboard" && (
          <>
            {connBanner}
            <Dashboard
              servers={servers}
              thresholds={thresholds}
              pulses={pulses}
              search={search}
              setSearch={setSearch}
              statusFilter={statusFilter}
              roleFilter={roleFilter}
              setRoleFilter={setRoleFilter}
              view={t.view}
              density={t.density}
              onOpenServer={(s) => setOpenServerId(s.id)}
              onDeleteServer={(s) => setDeleteTarget(s)}
              onEditServer={(s) => setEditTarget(s)}
              onAddServer={() => setShowAdd(true)}
            />
          </>
        )}

        {route === "notifications" && (
          <SettingsPage
            settings={settings}
            onSave={saveSettings}
            lastSavedAt={lastSavedAt}
          />
        )}

        {route === "logs" && (
          <LogsPage
            logs={logs}
            loading={loadingLogs}
            error={logsError}
            refresh={refreshLogs}
          />
        )}
      </main>

      <ProcessPanel
        server={openServer}
        thresholds={thresholds}
        onClose={() => setOpenServerId(null)}
      />

      {showAdd && (
        <AddServerModal onClose={() => setShowAdd(false)} liveServers={liveServers} />
      )}
      {deleteTarget && (
        <DeleteModal
          server={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
        />
      )}
      {editTarget && (
        <EditServerModal
          server={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => setEditTarget(null)}
        />
      )}

      <TweaksPanel title="Tweaks">
        <TweakSection title="Tampilan">
          <TweakRadio
            label="Tema"
            value={t.theme}
            onChange={(v) => setTweak("theme", v)}
            options={[{ value: "dark", label: "Dark" }, { value: "light", label: "Light" }]}
          />
          <TweakRadio
            label="View"
            value={t.view}
            onChange={(v) => setTweak("view", v)}
            options={[{ value: "table", label: "Tabel" }, { value: "cards", label: "Kartu" }]}
          />
          <TweakRadio
            label="Density"
            value={t.density}
            onChange={(v) => setTweak("density", v)}
            options={[
              { value: "compact", label: "Padat" },
              { value: "cozy", label: "Cozy" },
              { value: "comfortable", label: "Lapang" },
            ]}
          />
          <TweakSelect
            label="Aksen"
            value={t.accent}
            onChange={(v) => setTweak("accent", v)}
            options={[
              { value: "teal", label: "Teal" },
              { value: "indigo", label: "Indigo" },
              { value: "lime", label: "Lime" },
              { value: "orange", label: "Orange" },
            ]}
          />
        </TweakSection>
        <TweakSection title="Real-time">
          <TweakSlider
            label="Update interval"
            value={t.tickSeconds}
            onChange={(v) => setTweak("tickSeconds", v)}
            min={1}
            max={10}
            step={1}
            suffix="s"
          />
          <TweakToggle
            label="Sidebar expanded"
            value={t.sidebarExpanded}
            onChange={(v) => setTweak("sidebarExpanded", v)}
          />
        </TweakSection>
      </TweaksPanel>
    </div>
  );
}

function Banner({ tone, children }) {
  const colors = {
    info: { bg: "var(--accent-soft)", border: "var(--accent)", text: "var(--text)" },
    warn: { bg: "var(--warn-soft)", border: "var(--warn)", text: "var(--text)" },
    crit: { bg: "var(--crit-soft)", border: "var(--crit)", text: "var(--text)" },
  }[tone] || { bg: "var(--bg-elev)", border: "var(--border)", text: "var(--text)" };
  return (
    <div
      role="status"
      style={{
        margin: "8px 28px 0",
        padding: "10px 14px",
        borderRadius: 10,
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        color: colors.text,
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}
